import { ECONOMY } from '@/config/economy';
import { sectorAt, type SectorDef } from '@/config/sectors';
import { STARTING_WEAPON } from '@/config/weapons';
import { emptyStats, type RunStats } from '@/core/records';
import { Rng } from '@/core/rng';
import type { BossRotation, FightPick } from '@/bosses/rotation';
import type { CardDef } from '@/upgrades/cards';
import type { OfferCard } from '@/upgrades/pool';

/** Run kind: one campaign sector, or the endless rush (docs/01). */
export type RunMode = 'sector' | 'endless';

/** How a fresh run starts (title → sector select → slot). */
export interface RunSetup {
  mode: RunMode;
  /** Sector index (ignored by endless runs) */
  sector: number;
  /** Seed pin from the title picker (null = ?seed= or random) */
  seed: number | null;
  /** Starting weapon (meta unlock, docs/01 achievements) */
  weapon: string;
}

/** Builds the boss rotation of a run: null = endless, else a sector index. */
export type RotationFactory = (sector: number | null) => BossRotation;

/** Minion-wave progress inside the current room (docs/12 C8). */
export interface WaveState {
  /** Waves this room runs before the boss */
  total: number;
  /** Current wave; == total once the boss is out (saved as the checkpoint) */
  index: number;
  /** Minions the current wave started with (HUD progress bar) */
  spawned: number;
  /** The boss waits offstage until the waves clear */
  bossHeld: boolean;
  /** Countdown to the next mid-fight side squad (docs/12 M2) */
  addTimer: number;
  /** Stall failsafe clock: a wave that drags detonates itself (N hotfix) */
  stallTimer: number;
}

/**
 * Everything a run IS, as plain data — no Pixi, no systems. Serialized to
 * and from SaveData in one place (run/run-save.ts). The scene (world) and
 * the flows read and write it; the ship's own state lives on Player.
 *
 * Sector campaign (EPIC D): a sector run counts its rooms from 0 (the
 * finale is room `sector.rooms`); an endless run counts forever. Read the
 * counter via `roomInSector` (structure: waves, gates, prices) or `depth`
 * (boss draw and level) — both equal `roomNumber` in either mode, the
 * sector table supplies the difficulty step (run/sector-hooks.ts).
 */
export class RunState {
  /** Run seed — also keys the previewable wave plans (run/wave-plan.ts) */
  seed: number;
  rng: Rng;
  mode: RunMode = 'endless';
  /** Sector index of a sector run (0-based) */
  sectorIndex = 0;
  /** Boss draw — rebuilt for the mode/sector at every run start */
  rotation: BossRotation;
  roomNumber = 0;
  /** Index into LAYOUTS (wraps) */
  arenaIndex = 0;
  /** The fight in progress — death retries the SAME boss at the SAME level */
  currentFight: FightPick | null = null;
  /** Pre-picked NEXT fight — lets the shop preview it (docs/12 B5) */
  nextPick: FightPick | null = null;
  readonly wave: WaveState = {
    total: 0, index: 0, spawned: 0, bossHeld: false, addTimer: 0, stallTimer: 0,
  };
  /** Boss hp thresholds (descending) still pending a shield-drop roll */
  lootThresholds: number[] = [];

  // ---- Economy ----
  gems = 0;
  red = 0;
  /** Sub-gem remainder from the greed multiplier */
  gemFrac = 0;
  /** Damage accumulator for hit-drip gems (docs/12 E3) */
  dripAccum = 0;
  /** Fractional HP carried between lifesteal ticks */
  healFrac = 0;
  /** Armor plates stored in the hangar — applied on demand (docs/12 H3) */
  storedArmor = 0;
  rerollPrice: number = ECONOMY.rerollBase;
  healPrice: number = ECONOMY.heal.basePrice;
  /** Yellow-gem armor buy price — climbs per purchase, resets each break (O) */
  armorPrice: number = ECONOMY.armorBuy.basePrice;
  offer: OfferCard[] = [];

  // ---- Build ----
  /** Collected weapons — switchable during the shop break */
  inventory: string[] = [STARTING_WEAPON];
  /** Rotation pointer for full bays — each new crate replaces the NEXT bay */
  invRotation = 0;
  /** Crate picked up with a full bag — swap or scrap at the break (docs/09) */
  pendingWeapon: string | null = null;
  /** Weapons picked up since the last break ("new" sparkle, not saved) */
  freshWeapons: string[] = [];
  /** Card collection — persists across deaths minus the death tax (docs/03) */
  ownedCards: CardDef[] = [];
  stats: RunStats = emptyStats();
  /** Attempt counter for the active slot — +1 on every death (docs/12 C3) */
  totalRuns = 1;

  // ---- Death / continue ----
  /** True while the post-death upgrade bay is open (docs/12 #6): CONTINUE
   *  restarts the SAME arena instead of advancing to the next. */
  deathShop = false;
  /** Death-tax card candidates (indices into ownedCards) */
  discardOffer: number[] = [];
  /** Weapon offered as a death-tax candidate (docs/12 C4/F3) */
  deathWeaponId: string | null = null;

  // ---- Sector campaign (achievements + victory, docs/01) ----
  /** Hits the ship took since the room's boss came out ("untouchable") */
  bossHits = 0;
  /** Achievements earned during this run (victory screen) */
  earned: string[] = [];
  /** Countdown from the finale kill to the victory screen (0 = idle) */
  victoryTimer = 0;

  constructor(private readonly rotations: RotationFactory, seed: number) {
    this.seed = seed;
    this.rng = new Rng(seed);
    this.rotation = rotations(null);
  }

  /** The sector of a sector run (null in endless). */
  get sector(): SectorDef | null {
    return this.mode === 'sector' ? sectorAt(this.sectorIndex) : null;
  }

  /** Room index inside the current sector (endless: the raw counter). */
  get roomInSector(): number {
    return this.roomNumber;
  }

  /** Global run depth — the boss draw/level input (= roomNumber). */
  get depth(): number {
    return this.roomNumber;
  }

  /** This room is the sector FINALE (never true in endless). */
  get inFinale(): boolean {
    const sector = this.sector;
    return sector !== null && this.roomNumber >= sector.rooms;
  }

  /** Switch run kind; the rotation follows (reset by the caller's restore). */
  setMode(mode: RunMode, sectorIndex: number): void {
    this.mode = mode;
    this.sectorIndex = mode === 'sector' ? sectorIndex : 0;
    this.rotation = this.rotations(mode === 'sector' ? this.sectorIndex : null);
  }

  /** The death bay retries the SAME room; a shop break leads to the next one. */
  get nextRoomStep(): number {
    return this.deathShop ? 0 : 1;
  }

  reseed(seed: number): void {
    this.seed = seed;
    this.rng = new Rng(seed);
  }

  /** Draw + consume the fight for depth `room` (bag/history advance HERE). */
  drawFight(room: number): FightPick {
    return this.rotation.next(this.rng, room);
  }

  /**
   * New run (sector start or endless): progress, economy and build back to
   * the start — a FRESH build, nothing carries over (docs/01).
   */
  resetForNewGame(seed: number, setup?: Omit<RunSetup, 'seed'>): void {
    this.setMode(setup?.mode ?? 'endless', setup?.sector ?? 0);
    this.clearTransient();
    this.totalRuns = 1;
    this.ownedCards = [];
    this.reseed(seed);
    this.gems = 0;
    this.red = 0;
    this.storedArmor = 0;
    this.inventory = [setup?.weapon ?? STARTING_WEAPON];
    this.pendingWeapon = null;
    this.stats = emptyStats();
    this.rotation.reset();
    this.currentFight = null;
    this.nextPick = null;
    this.roomNumber = 0;
  }

  /**
   * Per-session leftovers that are never saved — cleared on every run start
   * AND on Continue, so nothing leaks from the run played before (drip and
   * lifesteal remainders, the full-bay pointer, a stale shop offer).
   */
  clearTransient(): void {
    this.gemFrac = 0;
    this.dripAccum = 0;
    this.healFrac = 0;
    this.offer = [];
    this.invRotation = 0;
    this.freshWeapons = [];
    this.deathShop = false;
    this.discardOffer = [];
    this.deathWeaponId = null;
    this.bossHits = 0;
    this.earned = [];
    this.victoryTimer = 0;
  }
}
